import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCartState , getCurrentCartState} from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { OrderConfirmationPageContext } from "@insite/content-library/Pages/OrderConfirmationPage";
import React, { FC, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";

const mapStateToProps = (state: ApplicationState) => {
    const confirmation = state.pages.orderConfirmation;
    const cartState = confirmation.cartId ? getCartState(state, confirmation.cartId) : getCurrentCartState(state);

    const cart = cartState?.value;

    const session = state.context.session;
    const billToId = session?.billToId;
    const shipToId = cart?.shipToId ?? session?.shipToId;

    // Pull BillTo and ShipTo directly from data stores
    const billTosById = state?.data?.billTos?.byId || {};
    const shipTosById = state?.data?.shipTos?.byId || {};

    const billTo = billToId ? billTosById[billToId] : undefined;
    const shipTo = shipToId ? shipTosById[shipToId] : undefined;

    return {
        cart,
        session,
        billTo,
        shipTo,
    };
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps>;

const OrderConfirmationBluecorePurchasePixel: FC<Props> = ({ cart, session, billTo, shipTo }) => {
    if (!cart) {return null;}

    // --- Email: prefer session, then BillTo, then initiatedByUserName (as last resort) ---
    const email =
        (session?.email || billTo?.email || cart?.initiatedByUserName || "").trim();

    // --- product_ids ---
    const productIds =
        cart.cartLines?.map(li => li.erpNumber).filter(Boolean).map(String) || [];

    // --- Phone : BillTo first, then ShipTo ---
    const phone =
        (billTo?.phone && String(billTo.phone).trim()) ||
        (shipTo?.phone && String(shipTo.phone).trim()) ||
        "";

    // --- Order & Total ---
    const orderId = String(cart.orderNumber);
    const total = cart.orderGrandTotal || 0;

    // --- Build pixel URL (URL-encoded) ---
    const src = useMemo(() => {
        const params = new URLSearchParams();
        params.set("token", "test_equity"); // fixed token for your site
        params.set("email", email);
        params.set("total", String(total));
        params.set("order_id", orderId);
        params.set("product_ids", productIds.join(","));
        if (phone) {params.set("phone_number", phone);}
        return `https://api.bluecore.app/api/track/purchase_pixel?${params.toString()}`;
    }, [email, total, orderId, productIds, phone]);

    // --- De-duplicate per order in this browser session ---
    const [shouldRender, setShouldRender] = useState(true);
    useEffect(() => {
        try {
            const key = `bluecore_purchase_fired_${orderId}`;
            if (sessionStorage.getItem(key)) {
                setShouldRender(false);
                return;
            }
            sessionStorage.setItem(key, "1");
        } catch {
            // ignore storage errors 
        }
    }, [orderId]);

    if (!shouldRender) {return null;}

    return (
        <img
            id="bluecore_purchase_pixel"
            alt="bluecore purchase pixel"
            src={src}
            style={{ display: "none" }}
        />
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(OrderConfirmationBluecorePurchasePixel),
    definition: {
        displayName: "Bluecore Purchase Pixel",
        allowedContexts: [OrderConfirmationPageContext],
        group: "Order Confirmation",
        fieldDefinitions: [], // no UI fields
    },
};

export default widgetModule;